import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Banner, Button, ButtonLink, Card, Dialog, Field, formatMoney, mulMoney, PageHeader, type LinkComponent,
} from '@elysian/ui';
import type { Account, BankAccountSummary, Contact, ContactProduct, DocumentInput, DocumentSummary, FxRate, LedgerDocument, Product, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { CurrencyOptions, useYourCurrencies } from '../shared/CurrencyPicker';
import { addDays, draftTotals, dueDateFor, isAmount, iso, shortDate, termsText, type DraftLine } from './shared';

const RouterLink = Link as unknown as LinkComponent;
let keySeq = 0;
const newKey = () => `l${++keySeq}`;

interface Form {
  contact_id: string;
  issue_date: string;
  /** The supplier's own invoice or credit note number (bills and supplier credits). */
  supplier_ref: string;
  /** A bank, card or cash account the bill was paid from there and then; empty: to pay later. */
  paid_from: string;
  due_date: string;
  reference: string;
  notes: string;
  currency: string;
  /** A rate typed for this document only; empty means the day's rate. */
  rate: string;
  lines: DraftLine[];
}

const BASE = 'EUR';
const SOURCE_NAME: Record<FxRate['source'], string> = {
  base: '', manual: 'your typed rate', ecb: 'ECB', hmrc: 'HMRC', wise: 'Wise', revolut: 'Revolut',
};

interface Loaded {
  customers: Contact[]; accounts: Account[]; codes: TaxCode[]; doc: LedgerDocument | null; from: LedgerDocument | null;
  banks: BankAccountSummary[]; others: DocumentSummary[]; products: Product[];
}
type DocType = 'sales_invoice' | 'sales_credit' | 'purchase_bill' | 'purchase_credit';

/** Where each kind of document lives, and what it's called. */
export const salesPaths = (type: string) => (type === 'sales_credit'
  ? { base: '/credit-notes', noun: 'credit note', Noun: 'Credit note', list: 'All credit notes' }
  : type === 'purchase_bill'
    ? { base: '/bills', noun: 'bill', Noun: 'Bill', list: 'All bills' }
    : type === 'purchase_credit'
      ? { base: '/bills', noun: 'supplier credit note', Noun: 'Supplier credit note', list: 'All bills' }
      : { base: '/invoices', noun: 'invoice', Noun: 'Invoice', list: 'All invoices' });

const isPurchase = (t: DocType) => t === 'purchase_bill' || t === 'purchase_credit';
const normRef = (v: string | null | undefined) => (v ?? '').replace(/[^a-z0-9]/gi, '').toLowerCase();

/**
 * New invoice, credit note, bill or supplier credit note, or a draft being
 * edited. A new invoice can start from ?customer=…, a bill from ?supplier=…;
 * a credit note from ?invoice=… and a supplier credit from ?bill=… (contact and
 * lines copied). A bill can be marked paid there and then from a bank, card or
 * cash account.
 */
export function InvoiceEditor({ type = 'sales_invoice' }: { type?: DocType }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { source, today } = useSession();
  const now = iso(today());
  const purchase = isPurchase(type);
  const fromInvoice = params.get(purchase ? 'bill' : 'invoice');
  const p = salesPaths(type);
  const data = useAsync<Loaded>(async () => {
    const [customers, accounts, codes, doc, from, banks, others, products] = await Promise.all([
      source.contacts(purchase ? 'supplier' : 'customer'), source.accounts(purchase ? 'expense' : 'income'), source.taxCodes(now),
      id ? source.document(id) : Promise.resolve(null),
      !id && fromInvoice ? source.document(fromInvoice) : Promise.resolve(null),
      type === 'purchase_bill' ? source.bankAccounts() : Promise.resolve([]),
      purchase ? source.documents({ type }) : Promise.resolve([]),
      source.products().catch(() => [] as Product[]),
    ]);
    return { customers, accounts, codes: codes.filter((c) => c.applies_to !== (purchase ? 'sales' : 'purchases')), doc, from, banks, others, products };
  }, [source, id, fromInvoice, type]);

  if (data.state === 'loading') return <PageHeader title={id ? 'Edit draft' : `New ${p.noun}`} subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title="Invoice" /><Banner>Couldn’t load: {data.error.message}</Banner></>;
  if (data.data.doc && data.data.doc.status !== 'draft') {
    return (
      <>
        <PageHeader title={data.data.doc.doc_no ?? p.Noun} />
        <Banner action={<Link to={`${p.base}/${data.data.doc.id}`}>Back to the {p.noun}</Link>}>
          Posted {p.noun}s can’t be edited. Void it and {purchase ? 'enter it again' : 'raise a new one'}{type === 'sales_invoice' ? ', or issue a credit note' : ''}.
        </Banner>
      </>
    );
  }
  // Editing a draft: the draft says what it is (a bill or a supplier credit share one route).
  const docType = (data.data.doc?.type ?? type) as DocType;
  return <EditorForm key={id ?? 'new'} {...data.data} now={now} type={isPurchase(docType) === purchase ? docType : type}
    customerId={params.get(purchase ? 'supplier' : 'customer')} />;
}

function EditorForm({ customers, accounts, codes, doc, from, banks, others, products, now, type, customerId }: Loaded & { now: string; type: DocType; customerId: string | null }) {
  const p = salesPaths(type);
  const purchase = isPurchase(type);
  const who = purchase ? 'supplier' : 'customer';
  const Who = purchase ? 'Supplier' : 'Customer';
  const hasDue = type === 'sales_invoice' || type === 'purchase_bill';
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const navigate = useNavigate();
  const defaultAccount = accounts.find((a) => a.code === (purchase ? '5000' : '4010')) ?? accounts[0];
  const blankLine = (): DraftLine => ({
    key: newKey(), description: '', quantity: '1', unit_price: '', account_code: defaultAccount?.code ?? '', tax_code: defaultAccount?.default_tax_code ?? 'S23',
  });

  const [form, setForm] = useState<Form>(() => doc
    ? {
      contact_id: doc.contact_id, issue_date: doc.issue_date, due_date: doc.due_date ?? doc.issue_date,
      supplier_ref: doc.supplier_ref ?? '', paid_from: '',
      reference: doc.reference ?? '', notes: doc.notes ?? '', currency: doc.currency, rate: '',
      lines: doc.lines.map((l) => ({
        key: newKey(), description: l.description, quantity: trim(l.quantity ?? '1'), unit_price: trim(l.unit_price),
        account_code: l.account_code, tax_code: l.tax_code ?? 'S23', product_code: l.product_code ?? undefined,
      })),
    }
    : from
      ? {
        contact_id: from.contact_id, issue_date: now, due_date: now, supplier_ref: '', paid_from: '',
        reference: purchase ? '' : from.doc_no ?? '', notes: `Credit against ${(purchase ? from.supplier_ref : null) ?? from.doc_no ?? p.noun}.`,
        currency: from.currency, rate: from.currency === BASE ? '' : trimRate(from.fx_rate ?? ''),
        lines: from.lines.map((l) => ({
          key: newKey(), description: `Credit: ${l.description}`, quantity: trim(l.quantity ?? '1'), unit_price: trim(l.unit_price),
          account_code: l.account_code, tax_code: l.tax_code ?? 'S23', product_code: l.product_code ?? undefined,
        })),
      }
      : {
        contact_id: customers.some((c) => c.id === customerId) ? customerId! : '', issue_date: now, due_date: addDays(now, 30),
        supplier_ref: '', paid_from: '', reference: '', notes: '',
        currency: customers.find((c) => c.id === customerId)?.currency ?? BASE, rate: '', lines: [blankLine()],
      });
  const [dueTouched, setDueTouched] = useState(!!doc);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const customer = customers.find((c) => c.id === form.contact_id);
  // Same supplier, same number: probably entered already (the bill inbox does the same check).
  const duplicate = purchase && form.supplier_ref.trim()
    ? others.find((o) => o.id !== doc?.id && o.status !== 'void' && o.contact_id === form.contact_id && normRef(o.supplier_ref) === normRef(form.supplier_ref))
    : undefined;
  const yourCurrencies = useYourCurrencies();
  const terms = customer?.payment_terms_days ?? 30;
  const basis = customer?.payment_terms_basis ?? 'invoice_date';
  const totals = useMemo(() => draftTotals(form.lines, codes), [form.lines, codes]);
  const foreign = form.currency !== BASE;
  const [dayRate, setDayRate] = useState<FxRate | null | 'none' | 'loading'>(null);
  const typedRate = form.rate.trim();
  const rateOk = !typedRate || (/^\d+(\.\d{1,8})?$/.test(typedRate) && Number(typedRate) > 0);
  const rateInUse = typedRate && rateOk ? typedRate : dayRate && typeof dayRate === 'object' ? String(dayRate.rate) : null;
  const rate6 = rateInUse ? Number(rateInUse).toFixed(6) : null;
  const money = (v: string) => formatMoney(v, form.currency);

  // The day's rate, as the ledger will pick it up (typed rate for the day, the bank's feed, ECB or HMRC).
  useEffect(() => {
    if (!foreign) { setDayRate(null); return; }
    let live = true;
    setDayRate('loading');
    source.fxRate(form.currency, form.issue_date).then(
      (r) => {
        if (!live) return;
        setDayRate(r);
        // A draft saved with its own rate keeps it.
        if (doc && doc.currency === form.currency && doc.issue_date === form.issue_date && doc.fx_rate
          && Math.abs(Number(doc.fx_rate) - r.rate) > 0.0000005) {
          setForm((f) => (f.rate ? f : { ...f, rate: trimRate(doc.fx_rate!) }));
        }
      },
      () => live && setDayRate('none'),
    );
    return () => { live = false; };
  }, [source, foreign, form.currency, form.issue_date, doc]);

  useEffect(() => {
    // #32: from the invoice date or from the end of its month, as the customer's terms say.
    if (!dueTouched) setForm((f) => ({ ...f, due_date: dueDateFor(f.issue_date, { payment_terms_days: terms, payment_terms_basis: basis }) }));
  }, [terms, basis, form.issue_date, dueTouched]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const chooseCustomer = (id: string) => setForm((f) => {
    const c = customers.find((x) => x.id === id);
    // A new document follows the customer's currency until lines are priced.
    const priced = f.lines.some((l) => l.unit_price.trim());
    return { ...f, contact_id: id, ...(!doc && !priced && c ? { currency: c.currency ?? BASE, rate: '' } : {}) };
  });
  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  // A customer's own prices and codes, used when an item goes on their invoice.
  const [theirs, setTheirs] = useState<ContactProduct[]>([]);
  useEffect(() => {
    if (purchase || !form.contact_id || products.length === 0) { setTheirs([]); return; }
    let live = true;
    source.contactProducts(form.contact_id).then((x) => live && setTheirs(x), () => live && setTheirs([]));
    return () => { live = false; };
  }, [source, purchase, form.contact_id, products.length]);

  // Choosing an item fills in the line from the price list (or the customer's own); anything can still be changed.
  const choose = (key: string, code: string) => {
    const item = products.find((x) => x.code === code);
    if (!item) { setLine(key, { product_code: undefined }); return; }
    const own = purchase ? undefined : theirs.find((t) => t.code === item.code);
    const price = purchase ? item.purchase_price : own?.price ?? item.sales_price;
    const account = purchase ? item.purchase_account : item.sales_account;
    const vat = purchase ? item.purchase_tax_code : item.sales_tax_code;
    setLine(key, {
      product_code: item.code,
      description: [own?.their_code, own?.their_description ?? item.description].filter(Boolean).join(' '),
      ...(price != null ? { unit_price: trim(price) } : {}),
      ...(account && accounts.some((a) => a.code === account) ? { account_code: account } : {}),
      ...(vat && codes.some((c) => c.code === vat) ? { tax_code: vat } : {}),
    });
  };
  const offered = products.filter((x) => x.is_active && (purchase ? x.purchase_price != null || x.purchase_account : x.sales_price != null || x.sales_account));
  const showItems = offered.length > 0 || form.lines.some((l) => l.product_code);

  const lineProblem = (l: DraftLine): Partial<Record<'description' | 'quantity' | 'unit_price', string>> => ({
    ...(!l.description.trim() ? { description: 'Describe the line' } : {}),
    ...(!isAmount(l.quantity) || Number(l.quantity) === 0 ? { quantity: 'Enter a quantity' } : {}),
    ...(!isAmount(l.unit_price) ? { unit_price: 'Enter a price such as 120.00' } : {}),
  });
  const problems = [
    ...(!form.contact_id ? [`Choose a ${who}.`] : []),
    ...(hasDue && !form.paid_from && form.due_date < form.issue_date ? ['The due date is before the issue date.'] : []),
    ...(form.lines.length === 0 ? ['Add at least one line.'] : []),
    ...(form.lines.some((l) => Object.keys(lineProblem(l)).length) ? ['Some lines need a description, quantity or price.'] : []),
    ...(foreign && !rateOk ? ['The exchange rate should be a number such as 1.1628.'] : []),
    ...(foreign && rateOk && !typedRate && dayRate === 'none' ? [`There’s no ${form.currency} rate for ${shortDate(form.issue_date)}. Type one in.`] : []),
  ];

  const input = (): DocumentInput => ({
    type,
    contact_id: form.contact_id,
    issue_date: form.issue_date,
    due_date: hasDue ? (form.paid_from ? form.issue_date : form.due_date) : undefined,
    reference: purchase ? undefined : form.reference.trim() || undefined,
    supplier_ref: purchase ? form.supplier_ref.trim() || undefined : undefined,
    notes: form.notes.trim() || undefined,
    currency: form.currency,
    fx_rate: foreign && typedRate ? typedRate : undefined,
    lines: form.lines.map((l) => ({
      description: l.description.trim(), quantity: l.quantity.trim(), unit_price: l.unit_price.trim(),
      account_code: l.account_code, tax_code: l.tax_code, ...(l.product_code ? { product_code: l.product_code } : {}),
    })),
  });

  async function save(post: boolean) {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const saved = doc ? await source.replaceDraft(doc.id, input()) : await source.createDraft(input());
      if (post) {
        const posted = await source.postDocument(saved.id, form.paid_from || undefined);
        toast(form.paid_from ? `${posted.doc_no} posted and paid from ${banks.find((b) => b.code === form.paid_from)?.name ?? form.paid_from}`
          : `${posted.doc_no} posted to the ledger`);
      } else {
        toast('Draft saved');
      }
      navigate(`${p.base}/${saved.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function remove() {
    if (!doc) return;
    setBusy(true);
    try {
      await source.deleteDraft(doc.id);
      toast('Draft deleted');
      navigate(p.base);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  const cancelTo = doc ? `${p.base}/${doc.id}` : p.base;
  return (
    <>
      <PageHeader
        title={doc ? `Edit draft ${p.noun}` : `New ${p.noun}`}
        subtitle={purchase ? `Our ${p.noun} number is given when you approve it. For a bill with a file, use the bill inbox: it reads the file for you.`
          : `The ${p.noun} number is given when you approve it, so numbers never have gaps.`}
        actions={
          <>
            <ButtonLink as={RouterLink} to={cancelTo} variant="ghost">Cancel</ButtonLink>
            <Button onClick={() => void save(false)} disabled={busy}>Save draft</Button>
            <Button variant="primary" onClick={() => void save(true)} disabled={busy}>{form.paid_from ? 'Approve as paid' : 'Approve and post'}</Button>
          </>
        }
      />
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      {duplicate && (
        <Banner action={<Link to={`/bills/${duplicate.id}`}>Open {duplicate.doc_no ?? 'it'}</Link>}>
          {customer?.name ?? 'This supplier'} already has a {p.noun} numbered {duplicate.supplier_ref}. Check it isn’t the same one before approving.
        </Banner>
      )}
      {tried && problems.length > 0 && (
        <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{problems.join(' ')}</div>
      )}
      <div className="el-stack">
        <Card padding="20px">
          <div className="el-form-grid">
            <Field id="inv-customer" label={Who} error={tried && !form.contact_id ? `Choose a ${who}` : null}
              hint={customer ? `${termsText(customer)} to pay${customer.email ? ` · ${customer.email}` : ''}` : undefined}>
              <select id="inv-customer" className="el-input" value={form.contact_id} onChange={(e) => chooseCustomer(e.target.value)}>
                <option value="">Choose a {who}…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field id="inv-issue" label={purchase ? 'Dated' : 'Issue date'}>
              <input id="inv-issue" type="date" className="el-input" value={form.issue_date} onChange={(e) => set('issue_date', e.target.value)} />
            </Field>
            {hasDue && !form.paid_from && (
            <Field id="inv-due" label="Due date" error={form.due_date < form.issue_date ? 'Before the issue date' : null}>
              <input id="inv-due" type="date" className="el-input" value={form.due_date}
                onChange={(e) => { setDueTouched(true); set('due_date', e.target.value); }} />
            </Field>
            )}
            {purchase ? (
              <Field id="inv-sref" label={type === 'purchase_bill' ? 'Their invoice number' : 'Their credit note number'} hint="As printed on it">
                <input id="inv-sref" className="el-input" value={form.supplier_ref} onChange={(e) => set('supplier_ref', e.target.value)} />
              </Field>
            ) : (
              <Field id="inv-ref" label="Customer’s reference" hint="Their PO or work order number">
                <input id="inv-ref" className="el-input" value={form.reference} onChange={(e) => set('reference', e.target.value)} />
              </Field>
            )}
            {type === 'purchase_bill' && banks.length > 0 && (
              <Field id="inv-paid" label="Paid" hint={form.paid_from ? 'Posted with its payment, so it shows as paid' : 'Paid later, from the bank screen'}>
                <select id="inv-paid" className="el-input" value={form.paid_from} onChange={(e) => set('paid_from', e.target.value)}>
                  <option value="">Not yet: pay later</option>
                  {banks.filter((b) => b.currency === form.currency || b.currency === BASE).map((b) => (
                    <option key={b.code} value={b.code}>Paid from {b.name}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field id="inv-currency" label="Currency">
              <select id="inv-currency" className="el-input" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value, rate: '' }))}>
                <CurrencyOptions base={yourCurrencies.base} yours={yourCurrencies.yours} />
              </select>
            </Field>
            {foreign && (
              <Field id="inv-rate" label={`Exchange rate (€ per 1 ${form.currency})`} error={rateOk ? null : 'A number such as 1.1628'}
                hint={typedRate ? 'Your rate for this document only. Clear it to use the day’s rate.' : rateHint(dayRate, form.currency)}>
                <input id="inv-rate" className="el-input el-num" inputMode="decimal" value={form.rate}
                  placeholder={dayRate && typeof dayRate === 'object' ? String(dayRate.rate) : dayRate === 'loading' ? '…' : 'Type a rate'}
                  onChange={(e) => set('rate', e.target.value)} />
              </Field>
            )}
          </div>
        </Card>

        <Card padding="20px 0 16px">
          <div className="el-card-head"><h2 className="el-h2">Lines</h2><span className="el-hint">VAT is worked out on each line</span></div>
          <div className="el-table-wrap">
            <table className="el-table el-lines" aria-label={`${p.Noun} lines`} style={{ minWidth: showItems ? 980 : 820 }}>
              <thead>
                <tr>
                  {showItems && <th scope="col">Item</th>}
                  <th scope="col" style={{ width: '32%' }}>Description</th><th scope="col">Account</th>
                  <th scope="col" className="el-num">Qty</th><th scope="col" className="el-num">Unit price</th>
                  <th scope="col">VAT rate</th><th scope="col" className="el-num">Net</th><th scope="col" className="el-num">VAT</th>
                  <th scope="col"><span className="el-vh">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => {
                  const p = tried ? lineProblem(l) : {};
                  const row = totals.rows[i]!;
                  return (
                    <tr key={l.key}>
                      {showItems && (
                        <td>
                          <select id={`line-item-${i}`} className="el-input" style={{ width: 150 }} value={l.product_code ?? ''} aria-label={`Line ${i + 1} item`}
                            onChange={(e) => choose(l.key, e.target.value)}>
                            <option value="">—</option>
                            {[...offered, ...products.filter((x) => x.code === l.product_code && !offered.includes(x))].map((x) => (
                              <option key={x.code} value={x.code}>{x.code}</option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td>
                        <input id={`line-desc-${i}`} className="el-input" value={l.description} aria-label={`Line ${i + 1} description`}
                          aria-invalid={!!p.description} onChange={(e) => setLine(l.key, { description: e.target.value })} />
                      </td>
                      <td>
                        <select id={`line-acc-${i}`} className="el-input" value={l.account_code} aria-label={`Line ${i + 1} account`}
                          onChange={(e) => {
                            const a = accounts.find((x) => x.code === e.target.value);
                            setLine(l.key, { account_code: e.target.value, ...(a?.default_tax_code ? { tax_code: a.default_tax_code } : {}) });
                          }}>
                          {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name.replace(/^Sales — /, '')}</option>)}
                        </select>
                      </td>
                      <td>
                        <input id={`line-qty-${i}`} className="el-input el-num" style={{ width: 76 }} inputMode="decimal" value={l.quantity}
                          aria-label={`Line ${i + 1} quantity`} aria-invalid={!!p.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} />
                      </td>
                      <td>
                        <input id={`line-price-${i}`} className="el-input el-num" style={{ width: 120 }} inputMode="decimal" value={l.unit_price}
                          placeholder="0.00" aria-label={`Line ${i + 1} unit price`} aria-invalid={!!p.unit_price}
                          onChange={(e) => setLine(l.key, { unit_price: e.target.value })} />
                      </td>
                      <td>
                        <select id={`line-vat-${i}`} className="el-input" value={l.tax_code} aria-label={`Line ${i + 1} VAT rate`}
                          onChange={(e) => setLine(l.key, { tax_code: e.target.value })}>
                          {codes.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                      </td>
                      <td className="el-num">{money(row.net)}</td>
                      <td className="el-num el-muted">{money(row.tax)}</td>
                      <td>
                        <Button variant="ghost" size="sm" aria-label={`Remove line ${i + 1}`} disabled={form.lines.length === 1}
                          onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((x) => x.key !== l.key) }))}>✕</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="el-spread" style={{ padding: '14px 20px 0', alignItems: 'flex-start' }}>
            <Button size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))}>Add line</Button>
            <dl className="el-totals" aria-label="Totals">
              <div><dt>Net</dt><dd className="el-num">{money(totals.net)}</dd></div>
              {[...totals.byCode].filter(([, v]) => v.net !== '0.0000').map(([c, v]) => (
                <div key={c} className="el-muted"><dt>VAT {codes.find((x) => x.code === c)?.name ?? c} on {money(v.net)}</dt><dd className="el-num">{money(v.tax)}</dd></div>
              ))}
              <div className="el-totals-grand"><dt>Total</dt><dd className="el-num">{money(totals.gross)}</dd></div>
              {foreign && rateInUse && rate6 && (
                <div className="el-muted"><dt>In euro at {rateInUse}</dt><dd className="el-num">{formatMoney(mulMoney(totals.gross, rate6), BASE)}</dd></div>
              )}
            </dl>
          </div>
        </Card>

        <Card padding="20px">
          <Field id="inv-notes" label={purchase ? 'Notes' : 'Notes for the customer'}
            hint={purchase ? 'For your own records' : 'Printed on the invoice when “Notes” is switched on in the invoice design'}>
            <textarea id="inv-notes" className="el-input" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </Field>
        </Card>

        {doc && (
          <div><Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={busy}>Delete draft</Button></div>
        )}
      </div>

      {confirmDelete && (
        <Dialog title="Delete this draft?" onClose={() => setConfirmDelete(false)}
          actions={<><Button variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button><Button variant="danger" onClick={() => void remove()} disabled={busy}>Delete draft</Button></>}>
          <p className="el-muted" style={{ margin: 0 }}>Drafts have no number yet, so deleting one leaves no gap.</p>
        </Dialog>
      )}
    </>
  );
}

/** Where the day's rate comes from, for the hint under the rate box. */
function rateHint(r: FxRate | null | 'none' | 'loading', ccy: string): string {
  if (r === 'loading' || r === null) return 'Looking up the day’s rate…';
  if (r === 'none') return `No ${ccy} rate for that day. Type one in.`;
  return `Leave blank to use ${SOURCE_NAME[r.source]} for ${shortDate(r.rate_date)}: €${r.rate} per ${ccy}`;
}

/** "1.16279070" → "1.1627907": a stored rate for editing. */
function trimRate(v: string): string {
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v;
}

/** "3.0000" → "3", "120.5000" → "120.50": tidy amounts for editing. */
function trim(v: string): string {
  if (!v.includes('.')) return v;
  const [w, f = ''] = v.split('.');
  const t = f.replace(/0+$/, '');
  return t.length === 0 ? w! : `${w}.${t.padEnd(2, '0')}`;
}
